import React, { useState, useRef, useEffect, useCallback } from "react";

import { ensureXiaolaiFont } from "../utils/ensureXiaolaiFont";
import { useAppI18n } from "../hooks/useAppI18n";
import { submitFeedback, type FeedbackCategory } from "../data/feedbackService";

import type { DiagramListItem } from "../data/diagramStorage";

interface ProjectShelfProps {
  isOpen: boolean;
  onClose: () => void;
  onNewProject: () => void;
  onSelectProject: (id: string) => void;
  onDeleteProject?: (id: string) => void;
  onRenameProject?: (id: string, name: string) => Promise<void>;
  diagrams: DiagramListItem[];
  currentDiagramId: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  userLabel?: string;
  userAvatarUrl?: string | null;
  userInitials?: string;
  onUserClick?: () => void;
}

// ─── Feedback category options ───────────────────────────────────────────────
const FEEDBACK_CATEGORY_DEFS: {
  value: FeedbackCategory;
  labelKey: string;
  icon: string;
}[] = [
  { value: "bug", labelKey: "projectShelf.catBug", icon: "bug_report" },
  { value: "feature", labelKey: "projectShelf.catFeature", icon: "lightbulb" },
  { value: "general", labelKey: "projectShelf.catOther", icon: "chat" },
];

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return `Today ${date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  } else if (diffDays === 1) {
    return `Yesterday ${date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  } else if (diffDays < 7) {
    return date.toLocaleDateString([], { weekday: "long" });
  }
  return date.toLocaleDateString();
};

export const ProjectShelf: React.FC<ProjectShelfProps> = ({
  isOpen,
  onClose,
  onNewProject,
  onSelectProject,
  onDeleteProject,
  onRenameProject,
  diagrams,
  currentDiagramId,
  isLoading,
  isAuthenticated,
  userLabel = "User name",
  userAvatarUrl,
  userInitials,
  onUserClick,
}) => {
  const { t } = useAppI18n();

  // Avatar image loading with retry (handles transient CDN failures)
  const [avatarImgFailed, setAvatarImgFailed] = useState(false);
  const [avatarRetrying, setAvatarRetrying] = useState(false);
  const avatarRetriesRef = useRef(0);
  const MAX_AVATAR_RETRIES = 2;

  useEffect(() => {
    setAvatarImgFailed(false);
    setAvatarRetrying(false);
    avatarRetriesRef.current = 0;
  }, [userAvatarUrl]);

  const handleAvatarError = useCallback(() => {
    if (avatarRetriesRef.current < MAX_AVATAR_RETRIES) {
      avatarRetriesRef.current += 1;
      setAvatarRetrying(true);
      const delay = 1500 * avatarRetriesRef.current;
      setTimeout(() => setAvatarRetrying(false), delay);
    } else {
      setAvatarImgFailed(true);
    }
  }, []);

  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuOpenAbove, setMenuOpenAbove] = useState(false);
  const [renameModal, setRenameModal] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [deleteModal, setDeleteModal] = useState<string | null>(null);
  const [renameInput, setRenameInput] = useState("");
  const menuRef = useRef<HTMLDivElement>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const skipNextEnterRef = useRef(false);

  // ─── Feedback form state ─────────────────────────────────────────────────
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackCategory, setFeedbackCategory] =
    useState<FeedbackCategory>("general");
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackResult, setFeedbackResult] = useState<
    "success" | "error" | null
  >(null);
  const feedbackTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Ensure Xiaolai (handwritten CJK) font is loaded for project titles
  useEffect(() => {
    ensureXiaolaiFont();
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    if (openMenuId === null) {
      return;
    }
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current && !menuRef.current.contains(target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openMenuId]);

  useEffect(() => {
    if (renameModal) {
      setRenameInput(renameModal.name);
      requestAnimationFrame(() => renameInputRef.current?.focus());
    }
  }, [renameModal]);

  const handleOpenRename = (e: React.MouseEvent, diagram: DiagramListItem) => {
    e.stopPropagation();
    setOpenMenuId(null);
    setRenameModal({ id: diagram.id, name: diagram.name });
  };

  const handleRenameConfirm = async () => {
    if (!renameModal || !onRenameProject || !renameInput.trim()) {
      setRenameModal(null);
      return;
    }
    await onRenameProject(renameModal.id, renameInput.trim());
    setRenameModal(null);
  };

  const handleOpenDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setOpenMenuId(null);
    setDeleteModal(id);
  };

  const handleDeleteConfirm = () => {
    if (deleteModal && onDeleteProject) {
      onDeleteProject(deleteModal);
      setDeleteModal(null);
    }
  };

  // ─── Feedback handlers ───────────────────────────────────────────────────
  const handleFeedbackOpen = () => {
    setFeedbackOpen(true);
    setFeedbackCategory("general");
    setFeedbackMessage("");
    setFeedbackResult(null);
    requestAnimationFrame(() => feedbackTextareaRef.current?.focus());
  };

  const handleFeedbackClose = () => {
    setFeedbackOpen(false);
    setFeedbackMessage("");
    setFeedbackResult(null);
  };

  const handleFeedbackSubmit = async () => {
    if (!feedbackMessage.trim() || feedbackSubmitting) {
      return;
    }
    setFeedbackSubmitting(true);
    setFeedbackResult(null);
    try {
      await submitFeedback({
        category: feedbackCategory,
        message: feedbackMessage.trim(),
        diagramId: currentDiagramId,
      });
      setFeedbackResult("success");
      // Auto-close after showing success
      setTimeout(() => {
        handleFeedbackClose();
      }, 1800);
    } catch (err) {
      console.error("[ProjectShelf] Feedback submit error:", err);
      setFeedbackResult("error");
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  return (
    <div
      ref={panelRef}
      id="ideaweave-project-panel"
      className="ideaweave-project-panel"
      role="dialog"
      aria-label={t("projectShelf.title")}
      aria-hidden={!isOpen}
    >
      {/* Warm paper background decorations */}
      <div className="ideaweave-project-panel__bg">
        <div className="ideaweave-project-panel__blob ideaweave-project-panel__blob--1" />
        <div className="ideaweave-project-panel__blob ideaweave-project-panel__blob--2" />
      </div>

      <div className="ideaweave-project-panel__header">
        <div className="ideaweave-project-panel__title">
          {t("projectShelf.title")}
        </div>
        <button
          type="button"
          className="ideaweave-project-panel__close"
          onClick={onClose}
          aria-label={t("projectShelf.close")}
        >
          <span className="material-symbols-outlined">close</span>
        </button>
      </div>

      <div className="ideaweave-project-panel__content">
        <button
          type="button"
          className="ideaweave-project-panel__new-btn"
          onClick={onNewProject}
        >
          <span className="material-symbols-outlined">add_circle</span>
          {t("projectShelf.newProject")}
        </button>

        <div className="ideaweave-project-panel__section">
          <div className="ideaweave-project-panel__section-title">
            <span>
              {isAuthenticated
                ? t("projectShelf.yourProjects")
                : t("projectShelf.localProjects")}
            </span>
            <span className="ideaweave-project-panel__count">
              {diagrams.length}
            </span>
          </div>

          {isLoading ? (
            <div className="ideaweave-project-panel__loading">
              {t("projectShelf.loadingProjects")}
            </div>
          ) : !isAuthenticated ? (
            <div className="ideaweave-project-panel__empty">
              <p>{t("projectShelf.signInHint")}</p>
              <button
                type="button"
                className="ideaweave-project-panel__signin-btn"
                onClick={onUserClick}
              >
                {t("projectShelf.signIn")}
              </button>
            </div>
          ) : diagrams.length === 0 ? (
            <div className="ideaweave-project-panel__empty">
              <p>{t("projectShelf.emptyHint")}</p>
            </div>
          ) : (
            diagrams.map((diagram) => (
              <div
                key={diagram.id}
                className={`ideaweave-project-panel__project-wrap ${
                  currentDiagramId === diagram.id
                    ? "ideaweave-project-panel__project-wrap--active"
                    : ""
                } ${
                  openMenuId === diagram.id
                    ? "ideaweave-project-panel__project-wrap--menu-open"
                    : ""
                }`}
              >
                <button
                  type="button"
                  className="ideaweave-project-panel__project"
                  onClick={() => onSelectProject(diagram.id)}
                  aria-current={currentDiagramId === diagram.id}
                >
                  <div className="ideaweave-project-panel__project-info">
                    <div className="ideaweave-project-panel__project-title">
                      {diagram.name}
                    </div>
                    <div className="ideaweave-project-panel__project-meta">
                      {t("projectShelf.lastUpdated")}{" "}
                      {formatDate(diagram.updated_at)}
                    </div>
                  </div>
                </button>
                {(onDeleteProject || onRenameProject) && (
                  <div
                    className="ideaweave-project-panel__project-actions"
                    ref={openMenuId === diagram.id ? menuRef : undefined}
                  >
                    <button
                      type="button"
                      className="ideaweave-project-panel__project-menu-trigger"
                      onClick={(e) => {
                        e.stopPropagation();
                        const isOpening = openMenuId !== diagram.id;
                        if (isOpening) {
                          // Calculate whether the menu should open upward
                          const triggerRect = (
                            e.currentTarget as HTMLElement
                          ).getBoundingClientRect();
                          const panelRect =
                            panelRef.current?.getBoundingClientRect();
                          if (panelRect) {
                            // Approximate menu height (~100px for 2 items + padding)
                            const menuHeight = 100;
                            // Footer height is ~80px, check against panel bottom minus footer
                            const footerHeight = 80;
                            const availableBelow =
                              panelRect.bottom -
                              footerHeight -
                              triggerRect.bottom;
                            setMenuOpenAbove(availableBelow < menuHeight);
                          }
                        }
                        setOpenMenuId((prev) =>
                          prev === diagram.id ? null : diagram.id,
                        );
                      }}
                      aria-label={t("projectShelf.projectOptions")}
                      aria-expanded={openMenuId === diagram.id}
                    >
                      <span className="material-symbols-outlined">
                        more_horiz
                      </span>
                    </button>
                    {openMenuId === diagram.id && (
                      <div
                        className={`ideaweave-project-panel__menu${
                          menuOpenAbove
                            ? " ideaweave-project-panel__menu--above"
                            : ""
                        }`}
                        role="menu"
                        aria-label={t("projectShelf.projectActions")}
                      >
                        {onRenameProject && (
                          <button
                            type="button"
                            className="ideaweave-project-panel__menu-item"
                            role="menuitem"
                            onClick={(e) => handleOpenRename(e, diagram)}
                          >
                            <span className="material-symbols-outlined">
                              edit
                            </span>
                            <span>{t("projectShelf.rename")}</span>
                          </button>
                        )}
                        {onDeleteProject && (
                          <button
                            type="button"
                            className="ideaweave-project-panel__menu-item ideaweave-project-panel__menu-item--danger"
                            role="menuitem"
                            onClick={(e) => handleOpenDelete(e, diagram.id)}
                          >
                            <span className="material-symbols-outlined">
                              delete
                            </span>
                            <span>{t("projectShelf.delete")}</span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Rename modal - product style */}
      {renameModal && (
        <div
          className="ideaweave-project-panel__modal-overlay"
          onClick={() => setRenameModal(null)}
          role="presentation"
        >
          <div
            className="ideaweave-project-panel__modal ideaweave-project-panel__modal--rename"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label={t("projectShelf.renameProject")}
          >
            <div className="ideaweave-project-panel__modal-title">
              {t("projectShelf.renameProject")}
            </div>
            <input
              ref={renameInputRef}
              type="text"
              className="ideaweave-project-panel__modal-input"
              value={renameInput}
              onChange={(e) => setRenameInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  if (e.nativeEvent.isComposing || skipNextEnterRef.current) {
                    skipNextEnterRef.current = false;
                    return;
                  }
                  handleRenameConfirm();
                }
                if (e.key === "Escape") {
                  setRenameModal(null);
                }
              }}
              onCompositionEnd={() => {
                skipNextEnterRef.current = true;
                setTimeout(() => {
                  skipNextEnterRef.current = false;
                }, 100);
              }}
              placeholder={t("projectShelf.projectName")}
              aria-label={t("projectShelf.projectName")}
            />
            <div className="ideaweave-project-panel__modal-actions">
              <button
                type="button"
                className="ideaweave-project-panel__modal-btn ideaweave-project-panel__modal-btn--secondary"
                onClick={() => setRenameModal(null)}
              >
                {t("projectShelf.cancel")}
              </button>
              <button
                type="button"
                className="ideaweave-project-panel__modal-btn ideaweave-project-panel__modal-btn--primary"
                onClick={handleRenameConfirm}
                disabled={!renameInput.trim()}
              >
                {t("projectShelf.save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm modal - product style */}
      {deleteModal && (
        <div
          className="ideaweave-project-panel__modal-overlay"
          onClick={() => setDeleteModal(null)}
          role="presentation"
        >
          <div
            className="ideaweave-project-panel__modal ideaweave-project-panel__modal--delete"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label={t("projectShelf.delete")}
          >
            <div className="ideaweave-project-panel__modal-title">
              {t("projectShelf.deleteConfirmTitle")}
            </div>
            <p className="ideaweave-project-panel__modal-text">
              {t("projectShelf.deleteConfirmText")}
            </p>
            <div className="ideaweave-project-panel__modal-actions">
              <button
                type="button"
                className="ideaweave-project-panel__modal-btn ideaweave-project-panel__modal-btn--secondary"
                onClick={() => setDeleteModal(null)}
              >
                {t("projectShelf.cancel")}
              </button>
              <button
                type="button"
                className="ideaweave-project-panel__modal-btn ideaweave-project-panel__modal-btn--danger"
                onClick={handleDeleteConfirm}
              >
                {t("projectShelf.delete")}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="ideaweave-project-panel__footer">
        <button
          type="button"
          className="ideaweave-project-panel__user"
          onClick={onUserClick}
        >
          <span className="ideaweave-project-panel__user-icon">
            {avatarRetrying ? (
              <span className="ideaweave-project-panel__user-initials ideaweave-project-panel__user-initials--loading">
                {userInitials ?? "…"}
              </span>
            ) : userAvatarUrl && !avatarImgFailed ? (
              <img
                key={`shelf-avatar-${avatarRetriesRef.current}`}
                className="ideaweave-project-panel__user-avatar"
                src={userAvatarUrl}
                alt={userLabel}
                onError={handleAvatarError}
              />
            ) : userInitials ? (
              <span className="ideaweave-project-panel__user-initials">
                {userInitials}
              </span>
            ) : (
              <span className="material-symbols-outlined">person</span>
            )}
          </span>
          <span className="ideaweave-project-panel__user-name">
            {userLabel}
          </span>
        </button>
        <button
          type="button"
          className="ideaweave-project-panel__feedback"
          aria-label={t("projectShelf.feedback")}
          onClick={handleFeedbackOpen}
        >
          <span className="material-symbols-outlined">feedback</span>
        </button>
      </div>

      {/* ─── Feedback Form Modal ──────────────────────────────────────────── */}
      {feedbackOpen && (
        <div
          className="ideaweave-project-panel__modal-overlay"
          onClick={handleFeedbackClose}
          role="presentation"
        >
          <div
            className="ideaweave-project-panel__modal ideaweave-project-panel__modal--feedback"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label={t("projectShelf.sendFeedback")}
          >
            {/* Success state */}
            {feedbackResult === "success" ? (
              <div className="ideaweave-feedback__success">
                <span className="material-symbols-outlined ideaweave-feedback__success-icon">
                  check_circle
                </span>
                <div className="ideaweave-feedback__success-title">
                  {t("projectShelf.feedbackThanks")}
                </div>
                <div className="ideaweave-feedback__success-subtitle">
                  {t("projectShelf.feedbackReview")}
                </div>
              </div>
            ) : (
              <>
                <div className="ideaweave-project-panel__modal-title">
                  {t("projectShelf.sendFeedback")}
                </div>

                {/* Category selector */}
                <div className="ideaweave-feedback__categories">
                  {FEEDBACK_CATEGORY_DEFS.map((cat) => (
                    <button
                      key={cat.value}
                      type="button"
                      className={`ideaweave-feedback__cat-btn ${
                        feedbackCategory === cat.value
                          ? "ideaweave-feedback__cat-btn--active"
                          : ""
                      }`}
                      onClick={() => setFeedbackCategory(cat.value)}
                    >
                      <span className="material-symbols-outlined">
                        {cat.icon}
                      </span>
                      <span>{t(cat.labelKey)}</span>
                    </button>
                  ))}
                </div>

                {/* Message textarea */}
                <textarea
                  ref={feedbackTextareaRef}
                  className="ideaweave-feedback__textarea"
                  value={feedbackMessage}
                  onChange={(e) => setFeedbackMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      handleFeedbackClose();
                    }
                    // Cmd/Ctrl + Enter to submit
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                      handleFeedbackSubmit();
                    }
                  }}
                  placeholder={
                    feedbackCategory === "bug"
                      ? t("projectShelf.feedbackBugPlaceholder")
                      : feedbackCategory === "feature"
                      ? t("projectShelf.feedbackFeaturePlaceholder")
                      : t("projectShelf.feedbackGeneralPlaceholder")
                  }
                  rows={4}
                  aria-label={t("projectShelf.feedback")}
                />

                {/* Error hint */}
                {feedbackResult === "error" && (
                  <div className="ideaweave-feedback__error">
                    {t("projectShelf.feedbackError")}
                  </div>
                )}

                {/* Actions */}
                <div className="ideaweave-project-panel__modal-actions">
                  <button
                    type="button"
                    className="ideaweave-project-panel__modal-btn ideaweave-project-panel__modal-btn--secondary"
                    onClick={handleFeedbackClose}
                  >
                    {t("projectShelf.cancel")}
                  </button>
                  <button
                    type="button"
                    className="ideaweave-project-panel__modal-btn ideaweave-project-panel__modal-btn--primary"
                    onClick={handleFeedbackSubmit}
                    disabled={!feedbackMessage.trim() || feedbackSubmitting}
                  >
                    {feedbackSubmitting
                      ? t("projectShelf.sending")
                      : t("projectShelf.send")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
